/**
 * Transparent mobile header — scroll state only.
 *
 * The transparent look itself is pure CSS, emitted by sections/mobile-navigation.liquid when the
 * setting is on. That CSS keys off the *absence* of `.transparent-off`, so the transparent state
 * is already correct on first paint and this script never causes a flash or a reflow.
 *
 * All this does is add `.transparent-off` to the header once the first section of the page has
 * scrolled out of view, and take it away again on the way back up.
 *
 * Deliberately breakpoint-agnostic: every rule that reads `.transparent-off` sits inside a
 * max-width media query, so toggling the class is inert on desktop and there is nothing to
 * recalculate on resize.
 */
const MARKER_SELECTOR = '[data-transparent-mobile-header]';
const FIRST_SECTION_SELECTOR = '.content-for-layout > *';
const HEADER_ID = 'sticky-header';
const SOLID_CLASS = 'transparent-off';

let observer = null;

const setup = () => {
  observer?.disconnect();
  observer = null;

  // Removed by the theme editor when the setting is switched off mid-session.
  if (!document.querySelector(MARKER_SELECTOR)) return;

  const header = document.getElementById(HEADER_ID);
  const firstSection = document.querySelector(FIRST_SECTION_SELECTOR);
  if (!header || !firstSection) return;

  // No IntersectionObserver: settle on the solid header rather than a permanent overlay.
  if (!('IntersectionObserver' in window)) {
    header.classList.add(SOLID_CLASS);
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        header.classList.toggle(SOLID_CLASS, !entry.isIntersecting);
      }
    },
    { threshold: 0 },
  );

  observer.observe(firstSection);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setup);
} else {
  setup();
}

// The first section changes identity whenever sections are added, removed or reordered.
for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
  document.addEventListener(event, setup);
}
